import { useEffect, useState } from "react";

import { useAuth } from "./AuthProvider";
import { staffApi } from "../api/staffApi";
import { departmentsApi } from "../api/departmentsApi";
import { businessUnitsApi } from "../api/businessUnitsApi";

export interface MyDepartment {
  departmentId: string | null;
  departmentName: string | null;
  businessUnitId: string | null;
  businessUnitName: string | null;
  /**
   * Whether the signed-in member's own record is active. Null while loading
   * or if the member can't be resolved — callers should render nothing rather
   * than assume, since "unknown" and "inactive" mean very different things.
   */
  isActive: boolean | null;
  loading: boolean;
}

type Resolved = Omit<MyDepartment, "loading">;

const UNRESOLVED: Resolved = {
  departmentId: null,
  departmentName: null,
  businessUnitId: null,
  businessUnitName: null,
  isActive: null,
};

/**
 * One lookup per member, shared by every caller and kept for a minute. The
 * header resolves this on every page, and Home, the contract form and the
 * obligation panel ask for the same member at the same time — each used to
 * run its own three requests, two of them fetching the whole department and
 * business-unit lists just to find one name each.
 */
const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { at: number; value: Promise<Resolved> }>();

async function resolveMyDepartment(memberId: string): Promise<Resolved> {
  const res = await staffApi.searchStaff({
    ids: [memberId],
    pageIndex: 0,
    pageSize: 1,
  });
  const member = (res?.data || [])[0] || {};
  const departmentId: string | null = member.departmentId || null;
  const businessUnitId: string | null = member.businessUnitId || null;
  const isActive =
    typeof member.isActive === "boolean" ? member.isActive : null;

  // Names are resolved by id — department names in particular are not
  // unique, so matching on the name would pick the wrong one. Only the two
  // records are asked for, not the whole lists.
  let departmentName: string | null = null;
  let businessUnitName: string | null = null;
  try {
    const [deptRes, buRes] = await Promise.all([
      departmentId
        ? departmentsApi.searchDepartments({
            ids: [departmentId],
            pageIndex: 0,
            pageSize: 1,
          })
        : Promise.resolve({ data: [] }),
      businessUnitId
        ? businessUnitsApi.searchBusinessUnits({
            ids: [businessUnitId],
            pageIndex: 0,
            pageSize: 1,
          })
        : Promise.resolve({ data: [] }),
    ]);
    departmentName =
      (deptRes?.data || []).find((d: any) => d.id === departmentId)?.name ??
      null;
    businessUnitName =
      (buRes?.data || []).find((b: any) => b.id === businessUnitId)?.name ??
      null;
  } catch {
    // A missing name is not worth failing over — callers still get the
    // ids, and the scope itself is enforced server-side regardless.
  }

  return { departmentId, departmentName, businessUnitId, businessUnitName, isActive };
}

function loadMyDepartment(memberId: string): Promise<Resolved> {
  const hit = cache.get(memberId);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.value;
  const value = resolveMyDepartment(memberId);
  cache.set(memberId, { at: Date.now(), value });
  // A failed lookup is not remembered.
  value.catch(() => cache.delete(memberId));
  return value;
}

/**
 * The signed-in user's own department.
 *
 * /Auth/me returns roles but no department, so this resolves it from the
 * member record and then names it by id. Used for display only — what a
 * Department Admin is allowed to see is enforced server-side in
 * SearchContractQueryHandler, not here.
 */
export function useMyDepartment(enabled = true): MyDepartment {
  const { user } = useAuth();
  const [state, setState] = useState<MyDepartment>({
    ...UNRESOLVED,
    loading: enabled,
  });

  useEffect(() => {
    if (!enabled || !user?.id) {
      setState({ ...UNRESOLVED, loading: false });
      return;
    }

    let cancelled = false;
    loadMyDepartment(user.id)
      .then((resolved) => {
        if (!cancelled) setState({ ...resolved, loading: false });
      })
      .catch(() => {
        if (!cancelled) setState({ ...UNRESOLVED, loading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, user?.id]);

  return state;
}
